import { EntityType } from "@screencloud/signage-firestore-client";
import { useCallback, useEffect, useState } from "react";
import { CompareChangesModal } from "src/components/CompareChangesModal/CompareChangesModal";
import { CompareChangesStatus } from "src/components/CompareChangesModal/types";
import { useAppContext } from "src/hooks/useAppContext";
import { useCompareFileActions } from "src/hooks/useCompareFileActions";
import { subscribeToDocumentUpdates } from "src/state/liveUpdateManager";
import { useChannelCompareChanges } from "./hooks/useChannelCompareChanges";

export interface ChannelCompareChangesModalProps {
  channelId: string;
  onClose: () => void;
  /**
   * Performs the publish and resolves on success / rejects on failure. It must
   * NOT close the modal — the container closes on success and shows the error
   * state on failure.
   */
  onPublish: () => void | Promise<void>;
}

/**
 * Container for the Compare Changes modal on the Channel detail page
 * (GRWBKK-2041), mirroring PlaylistCompareChangesModal. The app modal manager
 * freezes the element it renders, so this component receives only stable props
 * and owns everything that changes while the modal is open: the selected zone,
 * live updates for both the channel and its draft, and the publish and retry
 * lifecycles.
 *
 * A failed comparison and a failed publish share one unified error state;
 * Retry either recomputes the diff or re-attempts the publish, depending on
 * which failed.
 */
export const ChannelCompareChangesModal = ({
  channelId,
  onClose,
  onPublish,
}: ChannelCompareChangesModalProps) => {
  const context = useAppContext();
  // Undefined means the main zone. The setter is wired to the zone selector
  // in GRWBKK-2040.
  const [selectedZone] = useState<string | undefined>(undefined);
  const { compare, draftId, loading, hasError, refetch } =
    useChannelCompareChanges({ channelId, selectedZone });
  const fileActions = useCompareFileActions();

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(false);
  // True only while an explicit Retry is refetching, so retrying from the error
  // state shows loading — a background realtime recalculation must not.
  const [isRetrying, setIsRetrying] = useState(false);

  const orgId = context.user.claims.orgId || "";

  useEffect(() => {
    const unsubscribers = [channelId, draftId]
      .filter((id): id is string => Boolean(id))
      .map((id) =>
        subscribeToDocumentUpdates(
          orgId,
          EntityType.CHANNEL,
          id,
          () => {
            // Background recalculation; a failure surfaces via `hasError` and
            // flips the modal to the error state.
            Promise.resolve(refetch()).catch(() => undefined);
          },
          true,
        ),
      );
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [orgId, channelId, draftId, refetch]);

  const handlePublish = useCallback(async () => {
    if (isPublishing) {
      return; // guard against a second submit while one is in flight
    }
    setPublishError(false);
    setIsPublishing(true);
    try {
      await onPublish();
      // onClose() unmounts this modal, so isPublishing is deliberately not
      // reset here.
      onClose();
    } catch {
      setPublishError(true);
      setIsPublishing(false);
    }
  }, [isPublishing, onPublish, onClose]);

  const handleRetry = useCallback(() => {
    if (publishError) {
      handlePublish();
      return;
    }
    setIsRetrying(true);
    // Clear the flag when the refetch settles rather than when `loading`
    // flips, so a refetch that never toggles `loading` (or rejects at once)
    // cannot leave the modal stuck on the skeleton. A failure surfaces via
    // `hasError`.
    Promise.resolve(refetch())
      .catch(() => undefined)
      .finally(() => setIsRetrying(false));
  }, [publishError, handlePublish, refetch]);

  // Mirror the modal's own no-change test: a result only has something to show
  // when it reports changes and has rows for them.
  const hasComparisonChanges =
    (compare?.changes.totalChanges ?? 0) > 0 &&
    (compare?.changes.items.length ?? 0) > 0;

  // An explicit Retry, or a blocking fetch with nothing to show yet, renders
  // the loading skeleton; a diff already on screen stays visible during a
  // background revalidation so it does not flash.
  const status: CompareChangesStatus =
    isRetrying || (loading && !hasComparisonChanges)
      ? "loading"
      : publishError || hasError
        ? "error"
        : "loaded";

  return (
    <CompareChangesModal
      status={status}
      currentlyLive={compare?.currentlyLive}
      changes={compare?.changes}
      isPublishing={isPublishing}
      onClose={onClose}
      onPublish={handlePublish}
      onRetry={handleRetry}
      {...fileActions}
    />
  );
};
